/**
 * AdminUnifiNetworkTab
 *
 * Admin tab for managing the UniFi Network controller integration.
 * Shows setup form when not connected, device/client/network tables when connected.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Wifi,
  Server,
  Monitor,
  Network,
  RefreshCw,
  Power,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Cable,
  Router,
  Eye,
  EyeOff,
  MapPin,
  UserCheck,
  UserX,
  Clock,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Ban,
} from 'lucide-react';
import {
  useUnifiNetworkStore,
  type UnifiNetworkDevice,
  type UnifiNetworkClient,
} from '@/integrations/unifi-network/unifi-network-store';
import { useAuthStore } from '@/stores/auth/auth-store';

// --- Helpers ---

function formatBps(bps?: number): string {
  if (bps == null || bps === 0) return '—';
  if (bps < 1000) return `${bps} bps`;
  if (bps < 1_000_000) return `${(bps / 1000).toFixed(1)} Kbps`;
  if (bps < 1_000_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  return `${(bps / 1_000_000_000).toFixed(2)} Gbps`;
}

function formatUptime(seconds?: number): string {
  if (seconds == null || seconds === 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function deviceStateColor(state: string): string {
  switch (state) {
    case 'ONLINE': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'OFFLINE': return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'PENDING_ADOPTION': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'UPDATING': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  }
}

function clientTypeColor(type: string): string {
  switch (type) {
    case 'WIRELESS': return 'bg-blue-500/10 text-blue-600';
    case 'WIRED': return 'bg-green-500/10 text-green-600';
    case 'VPN': return 'bg-purple-500/10 text-purple-600';
    default: return 'bg-gray-500/10 text-gray-600';
  }
}

function deviceTypeIcon(type: string) {
  switch (type) {
    case 'uap': return <Wifi className="h-4 w-4" />;
    case 'usw': return <Cable className="h-4 w-4" />;
    case 'ugw': return <Router className="h-4 w-4" />;
    case 'udm': return <Server className="h-4 w-4" />;
    default: return <Server className="h-4 w-4" />;
  }
}

function deviceTypeLabel(type: string): string {
  switch (type) {
    case 'uap': return 'AP';
    case 'usw': return 'Switch';
    case 'ugw': return 'Gateway';
    case 'udm': return 'Dream Machine';
    default: return type?.toUpperCase() || 'Unknown';
  }
}

// --- Editable Location Name ---

const EditableLocationName: React.FC<{ value: string; onSave: (name: string) => void }> = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (draft.trim() && draft !== value) onSave(draft.trim()); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { if (draft.trim() && draft !== value) onSave(draft.trim()); setEditing(false); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        autoFocus
        className="w-full rounded border border-input bg-background px-1.5 py-0.5 text-xs"
      />
    );
  }

  return (
    <span
      onClick={() => { setDraft(value); setEditing(true); }}
      className="cursor-pointer hover:text-foreground text-muted-foreground hover:underline"
      title="Click to edit location name"
    >
      <MapPin className="h-3 w-3 inline mr-0.5" />{value}
    </span>
  );
};

// --- Sub-components ---

const StatusBadge: React.FC<{ status: string; colorFn?: (s: string) => string }> = ({ status, colorFn }) => {
  const classes = colorFn ? colorFn(status) : 'bg-gray-500/10 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${classes}`}>
      {status}
    </span>
  );
};

const StatCard: React.FC<{ label: string; value: number | string; icon: React.ReactNode; color?: string }> = ({ label, value, icon, color = 'text-foreground' }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
    <div className={`p-2 rounded-md bg-muted ${color}`}>{icon}</div>
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </div>
);

// --- Setup View ---

const SetupView: React.FC = () => {
  const { saveSettings, isConnecting, error, clearError } = useUnifiNetworkStore();
  const [host, setHost] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [wsUsername, setWsUsername] = useState('');
  const [wsPassword, setWsPassword] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showWsPassword, setShowWsPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (!host.trim()) { setLocalError('Controller host or IP is required.'); return; }
    if (!apiKey.trim()) { setLocalError('API key is required.'); return; }

    try {
      await saveSettings(
        host.trim(),
        apiKey.trim(),
        wsUsername.trim() || undefined,
        wsPassword.trim() || undefined
      );
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Connection failed');
    }
  };

  const displayError = localError || error;

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
      <div className="flex flex-col items-center gap-2">
        <Wifi className="h-12 w-12 text-muted-foreground" />
        <h3 className="text-lg font-semibold">Configure UniFi Network Integration</h3>
        <p className="text-sm text-muted-foreground max-w-md text-center">
          Enter your UniFi controller hostname and API key. These credentials are
          stored securely and used by the edge device (Pi) on your local network
          to communicate with the controller. API keys can be generated in the
          UniFi Console under System Settings &rarr; Admins &amp; Users.
        </p>
      </div>

      {displayError && (
        <div className="w-full max-w-sm flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{displayError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <div>
          <label className="text-sm font-medium text-muted-foreground block mb-1.5">
            Controller Host / IP
          </label>
          <input
            type="text"
            placeholder="192.168.1.1"
            value={host}
            onChange={e => setHost(e.target.value)}
            disabled={isConnecting}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground block mb-1.5">
            API Key
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              placeholder="Enter your API key"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              disabled={isConnecting}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm pr-10"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="border-t border-border pt-4 mt-4">
          <p className="text-xs text-muted-foreground mb-3">
            Optional: Real-time presence detection via WebSocket. Create a local
            user with View Only role on the UniFi controller.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1.5">
                WebSocket Username
              </label>
              <input
                type="text"
                placeholder="Local user (View Only role)"
                value={wsUsername}
                onChange={e => setWsUsername(e.target.value)}
                disabled={isConnecting}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1.5">
                WebSocket Password
              </label>
              <div className="relative">
                <input
                  type={showWsPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={wsPassword}
                  onChange={e => setWsPassword(e.target.value)}
                  disabled={isConnecting}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowWsPassword(!showWsPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showWsPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <Button type="submit" disabled={isConnecting} className="w-full">
          {isConnecting ? (
            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            'Save Credentials'
          )}
        </Button>
      </form>

      <p className="text-xs text-muted-foreground text-center max-w-sm">
        Credentials are encrypted at rest. Your edge device (Raspberry Pi) on the
        local network will use these to connect to the UniFi controller.
        Self-signed SSL certificates are accepted.
      </p>
    </div>
  );
};

// --- WebSocket Credentials Section (shown in connected view) ---

const WsCredentialsSection: React.FC = () => {
  const { saveWsCredentials, hasWsCredentials, wsUsername: existingUsername } = useUnifiNetworkStore();
  const [expanded, setExpanded] = useState(false);
  const [wsUsername, setWsUsername] = useState(existingUsername || '');
  const [wsPassword, setWsPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync when store loads the existing username
  useEffect(() => {
    if (existingUsername && !wsUsername) {
      setWsUsername(existingUsername);
    }
  }, [existingUsername]);

  const handleSave = async () => {
    if (!wsUsername.trim() || !wsPassword.trim()) return;
    setSaving(true);
    try {
      await saveWsCredentials(wsUsername.trim(), wsPassword.trim());
      setSaved(true);
      setWsPassword('');
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // error handled by store
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <KeyRound className="h-4 w-4" />
        Real-Time Presence (WebSocket Credentials)
        {saved && <span className="ml-auto text-xs text-green-600">Saved</span>}
        {!saved && hasWsCredentials && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-green-600">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Configured
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            {hasWsCredentials
              ? 'WebSocket credentials are configured. Enter new values below to update them.'
              : 'Enter a local UniFi controller user with View Only role for real-time presence detection via WebSocket. This enables sub-second detection instead of polling.'
            }
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Username</label>
              <input
                type="text"
                placeholder="Local user"
                value={wsUsername}
                onChange={e => setWsUsername(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={wsPassword}
                  onChange={e => setWsPassword(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm pr-8"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !wsUsername.trim() || !wsPassword.trim()}
          >
            {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : null}
            Save WebSocket Credentials
          </Button>
        </div>
      )}
    </div>
  );
};

// --- Main Component ---

export const AdminUnifiNetworkTab: React.FC = () => {
  const {
    hasApiKey,
    isConnected,
    isConnecting,
    host,
    siteName,
    devices,
    clients,
    networks,
    trackedClients,
    presence,
    presenceLog,
    isLoading,
    error,
    loadSettings,
    loadDevices,
    loadClients,
    loadNetworks,
    restartDevice,
    powerCyclePort,
    removeSettings,
    updateDeviceLocation,
    trackClient,
    untrackClient,
    loadTrackedClients,
    excludeClient,
    includeClient,
    excludedClients,
    loadExcludedClients,
    loadPresence,
    loadPresenceLog,
    clearError,
  } = useUnifiNetworkStore();

  const [initialized, setInitialized] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval>>();

  // Load settings on mount
  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setInitialized(true);
    };
    init();
  }, [loadSettings]);

  // Load data when connected
  useEffect(() => {
    if (initialized && hasApiKey && isConnected) {
      loadDevices();
      loadClients();
      loadNetworks();
      loadTrackedClients();
      loadExcludedClients();
      loadPresence();
      loadPresenceLog();
    }
  }, [initialized, hasApiKey, isConnected, loadDevices, loadClients, loadNetworks, loadTrackedClients, loadExcludedClients, loadPresence, loadPresenceLog]);

  // Auto-refresh devices every 60s while connected (Pi pushes data, but we re-read from cache)
  useEffect(() => {
    if (hasApiKey && isConnected) {
      refreshTimer.current = setInterval(() => {
        loadDevices();
      }, 60000);
      return () => clearInterval(refreshTimer.current);
    }
  }, [hasApiKey, isConnected, loadDevices]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadDevices(), loadClients(), loadNetworks()]);
  }, [loadDevices, loadClients, loadNetworks]);

  const handleDisconnect = useCallback(async () => {
    if (window.confirm('Disconnect from UniFi Network and remove credentials?')) {
      await removeSettings();
    }
  }, [removeSettings]);

  const handleRestart = useCallback(async (device: UnifiNetworkDevice) => {
    if (window.confirm(`Restart "${device.name}"? This will cause a brief network interruption.`)) {
      await restartDevice(device.id);
    }
  }, [restartDevice]);

  const handlePoeCycle = useCallback(async (deviceId: string, portIdx: number) => {
    if (window.confirm(`Power-cycle PoE on port ${portIdx}? The connected device will restart.`)) {
      await powerCyclePort(deviceId, portIdx);
    }
  }, [powerCyclePort]);

  // Not loaded yet
  if (!initialized) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Setup view
  if (!hasApiKey) {
    return <SetupView />;
  }

  // Saved but no Pi reporting data yet
  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-yellow-500" />
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Credentials saved for {host}. Waiting for an edge device (Pi) on the local network
          to connect and report data. Make sure your Pi has <code className="text-xs bg-muted px-1 py-0.5 rounded">UNIFI_HOST</code> and <code className="text-xs bg-muted px-1 py-0.5 rounded">UNIFI_API_KEY</code> configured.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => useUnifiNetworkStore.getState().requestDiscovery()}>
            {isConnecting ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : null}
            Request Discovery
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDisconnect}>
            Remove
          </Button>
        </div>
      </div>
    );
  }

  // Connected view
  const onlineDevices = devices.filter(d => d.state === 'ONLINE').length;
  const offlineDevices = devices.filter(d => d.state === 'OFFLINE').length;

  return (
    <div className="h-full overflow-y-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Wifi className="h-5 w-5" /> UniFi Network
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {host} {siteName ? `· ${siteName}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-600 border border-green-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Connected
          </span>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-destructive hover:text-destructive">
            Disconnect
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError} className="text-destructive h-6 px-2">
            Dismiss
          </Button>
        </div>
      )}

      {/* WebSocket Credentials (for real-time presence) */}
      <WsCredentialsSection />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Devices" value={devices.length} icon={<Server className="h-4 w-4" />} />
        <StatCard label="Online" value={onlineDevices} icon={<CheckCircle2 className="h-4 w-4" />} color="text-green-600" />
        <StatCard label="Offline" value={offlineDevices} icon={<XCircle className="h-4 w-4" />} color={offlineDevices > 0 ? 'text-red-600' : 'text-muted-foreground'} />
        <StatCard label="Clients" value={clients.length} icon={<Monitor className="h-4 w-4" />} color="text-blue-600" />
      </div>

      {/* Presence */}
      {presence.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            <MapPin className="h-3.5 w-3.5 inline mr-1" /> Presence
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {presence.map(p => (
              <div key={p.clientMac || p.clientName} className={`bg-card border rounded-lg p-4 ${p.isPresent ? 'border-green-500/30' : 'border-border'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{p.clientName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.isPresent ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                          {p.locationName}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Away</span>
                      )}
                    </p>
                  </div>
                  {p.isPresent && <MapPin className="h-4 w-4 text-green-600" />}
                </div>
              </div>
            ))}
          </div>

          {/* Recent presence log */}
          {presenceLog.length > 0 && (
            <div className="mt-3 bg-muted/30 rounded-lg p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Recent Activity</p>
              <div className="space-y-1">
                {presenceLog.slice(0, 5).map((entry, i) => (
                  <div key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    <span className={entry.eventType === 'arrived' ? 'text-green-600' : entry.eventType === 'departed' ? 'text-red-600' : 'text-blue-600'}>
                      {entry.eventType}
                    </span>
                    <span>{entry.clientName} — {entry.locationName}</span>
                    <span className="ml-auto">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Devices */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Devices ({devices.length})
        </h3>
        {devices.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">No devices found.</div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Device</th>
                    <th className="text-left px-4 py-2 font-medium">Model</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">State</th>
                    <th className="text-left px-4 py-2 font-medium">IP</th>
                    <th className="text-left px-4 py-2 font-medium">MAC</th>
                    <th className="text-left px-4 py-2 font-medium">Firmware</th>
                    <th className="text-left px-4 py-2 font-medium">Location</th>
                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {devices.map(device => (
                    <tr key={device.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium">
                        <div className="flex items-center gap-2">
                          {deviceTypeIcon(device.type)}
                          {device.name}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{device.model}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-muted rounded font-medium">
                          {deviceTypeLabel(device.type)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={device.state} colorFn={deviceStateColor} />
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{device.ipAddress}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{device.macAddress}</td>
                      <td className="px-4 py-2 text-xs">
                        {device.firmwareVersion}
                        {device.firmwareUpdatable && (
                          <span className="ml-1 text-[10px] text-yellow-600">(update)</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <EditableLocationName
                          value={(device as any).locationName || device.name}
                          onSave={(name) => updateDeviceLocation(device.id, name)}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestart(device)}
                          disabled={device.state !== 'ONLINE'}
                          title="Restart device"
                          className="h-7 px-2"
                        >
                          <Power className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Clients */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Clients ({clients.length})
        </h3>
        {clients.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">No clients connected.</div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">IP</th>
                    <th className="text-left px-4 py-2 font-medium">MAC</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">Connected To</th>
                    <th className="text-left px-4 py-2 font-medium">Since</th>
                    <th className="text-right px-4 py-2 font-medium">Presence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {clients.map(client => {
                    // Resolve uplinkDeviceId to device name
                    const uplinkDevice = client.uplinkDeviceId
                      ? devices.find(d => d.id === client.uplinkDeviceId)
                      : undefined;

                    return (
                      <tr key={client.id} className="hover:bg-muted/30">
                        <td className="px-4 py-2 font-medium">
                          {client.name || client.hostname || '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{client.ipAddress}</td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{client.macAddress}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${clientTypeColor(client.type)}`}>
                            {client.type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {uplinkDevice ? (
                            <span className="inline-flex items-center gap-1">
                              {deviceTypeIcon(uplinkDevice.type)}
                              {uplinkDevice.name}
                            </span>
                          ) : client.uplinkDeviceId ? (
                            <span className="text-muted-foreground">{client.uplinkDeviceId.substring(0, 8)}...</span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {client.connectedAt ? new Date(client.connectedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            {trackedClients.some(t => t.clientMac === client.macAddress) ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-green-600"
                                onClick={() => untrackClient(client.macAddress)}
                                title="Stop tracking"
                              >
                                <UserCheck className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => {
                                  const userId = useAuthStore.getState().user?.id;
                                  if (userId) {
                                    trackClient(client.macAddress, userId, client.name || client.hostname || client.macAddress);
                                  }
                                }}
                                title="Track this device for presence"
                              >
                                <UserX className="h-3 w-3" />
                              </Button>
                            )}
                            {excludedClients.some(e => e.clientMac === client.macAddress.toLowerCase()) ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-red-500"
                                onClick={() => includeClient(client.macAddress)}
                                title="Include in presence counting"
                              >
                                <Ban className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-muted-foreground hover:text-red-500"
                                onClick={() => excludeClient(client.macAddress, client.name || client.hostname || client.macAddress)}
                                title="Exclude from presence counting"
                              >
                                <Ban className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Networks */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Networks ({networks.length})
        </h3>
        {networks.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">No networks found.</div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">VLAN ID</th>
                    <th className="text-left px-4 py-2 font-medium">Purpose</th>
                    <th className="text-left px-4 py-2 font-medium">Subnet</th>
                    <th className="text-left px-4 py-2 font-medium">DHCP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {networks.map(net => (
                    <tr key={net.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium flex items-center gap-2">
                        <Network className="h-4 w-4 text-muted-foreground" />
                        {net.name}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{net.vlanId ?? '—'}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{net.purpose || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs">{net.ipSubnet || '—'}</td>
                      <td className="px-4 py-2">
                        {net.dhcpEnabled != null ? (
                          net.dhcpEnabled
                            ? <span className="text-xs text-green-600">Enabled</span>
                            : <span className="text-xs text-muted-foreground">Disabled</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Edge Device Info */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Edge Device
        </h3>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Data collected by edge device (Pi)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your Raspberry Pi polls the local UniFi controller every 60s for devices
                and every 30s for status changes. Data is pushed to the server automatically.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => useUnifiNetworkStore.getState().requestDiscovery()}>
              <RefreshCw className="h-3 w-3 mr-1" /> Request Refresh
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
